import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Form,
  Select,
  Switch,
  Row,
  notification,
  Space,
  Checkbox,
  Typography,
  Col
} from 'antd'
import * as Icons from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Queries } from 'admin/lib/Api'
import { Guid } from 'admin/lib/guid'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { BodyContentColumn } from 'admin/lib/Layouts'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import * as ROUTES from 'admin/routes'
import { Matchers } from 'admin/lib/Records'
import { SectionProvider } from 'admin/lib/Forms'
import { FormProvider } from 'rc-field-form'
import { InsetSection } from 'admin/lib/Forms/Styled'

export function BIReportEditForm({ biReports, account, users }) {
  const navigate = useNavigate()
  const biReportsUserPermissions = Queries.useBIReportsUserPermissions(
    account.accountId
  )
  const biReportsWorkspaces = Queries.useBIReportsWorkspaces()

  const dashboardReport = getReport(biReports, 'dashboard')
  const professionalReport = getReport(biReports, 'professional')
  const upsertAccountBIReports = Queries.useAccountBIReportUpsert()
  const upsertAccountBIReportUserPermissions =
    Queries.useAccountBIReportUserPermissionsUpsert()
  const availableUsers = users.filter(us =>
    account.members.some(member => member.userId === us.userId)
  )
  //
  // States

  const [dashboardWorkspaceId, setDashboardWorkspaceId] = useState(
    dashboardReport.workspaceId
  )

  const [professionalWorkspaceId, setProfessionalWorkspaceId] = useState(
    professionalReport.workspaceId
  )
  const [hideQflowUsers, setHideQflowUsers] = useState(true)

  const biDashboardReports =
    Queries.useBIReportsWorkspaceReports(dashboardWorkspaceId)

  const biProfessionalReports = Queries.useBIReportsWorkspaceReports(
    professionalWorkspaceId
  )

  //
  // Form Setup

  const formValues = !biReportsUserPermissions.isFetched
    ? null
    : transformBIReportToFormData(
        dashboardReport,
        professionalReport,
        biReportsUserPermissions.data,
        availableUsers
      )

  const [form] = Form.useForm()

  //
  // Callbacks

  const navigateToList = useCallback(() => {
    navigate(ROUTES.BI_REPORTS.INDEX)
  }, [navigate])

  const onSubmit = useCallback(
    async values => {
      const workingKey = 'UPSERTING_ACCOUNT_BI_REPORT'
      notification.info({
        key: workingKey,
        icon: <Icons.LoadingOutlined />,
        message: 'Updating Account BI Report settings',
        duration: 0
      })
      let updatedReports = []
      let updatedUserPermissions = []

      const { biReports, userPermissions } = transformFormDataForSubmission(
        account.accountId,
        values,
        biDashboardReports.data,
        biProfessionalReports.data
      )

      upsertAccountBIReports.mutate(
        {
          accountId: account.accountId,
          biReports: biReports
        },
        {
          onSuccess: data => {
            updatedReports = data
            upsertAccountBIReportUserPermissions.mutate(
              {
                accountId: account.accountId,
                userPermissions: userPermissions
              },
              {
                onSuccess: data => {
                  updatedUserPermissions = data
                  const updatedDashboard = getReport(
                    updatedReports,
                    'dashboard'
                  )
                  const updatedProfessional = getReport(
                    updatedReports,
                    'professional'
                  )
                  const updatedFormValues = transformBIReportToFormData(
                    updatedDashboard,
                    updatedProfessional,
                    updatedUserPermissions,
                    availableUsers
                  )
                  form.setFieldsValue(updatedFormValues)
                  notification.success({
                    message: 'Successfully updated BIReports and Permissions'
                  })
                },
                onError: error => {
                  notification.error({
                    message: 'Error 😞 updating User Permissions',
                    description: (
                      <Typography.Text type="danger">
                        {JSON.stringify(error.response.data)}
                      </Typography.Text>
                    )
                  })
                },
                onSettled: () => {
                  notification.destroy(workingKey)
                }
              }
            )
          },
          onError: error => {
            notification.error({
              message: 'Error 😞 updating Reports',
              description: (
                <Typography.Text type="danger">
                  {JSON.stringify(error.response.data)}
                </Typography.Text>
              )
            })
          }
        }
      )
    },
    [
      account.accountId,
      availableUsers,
      biProfessionalReports,
      biDashboardReports,
      form,
      upsertAccountBIReportUserPermissions,
      upsertAccountBIReports
    ]
  )

  useGlobalShortcut(Matchers.form.save, () => onSubmit(form.submit()))

  const workspaceDropDown = (onChange = undefined) => (
    <Select loading={biReportsWorkspaces.isLoading} onChange={onChange}>
      {!biReportsWorkspaces.isLoading &&
        biReportsWorkspaces?.data
          .filter(ws => ws.name.toLowerCase() != 'datastorage')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(ws => (
            <Select.Option key={ws.workspaceId} value={ws.workspaceId}>
              {ws.name}
            </Select.Option>
          ))}
    </Select>
  )

  const dashboardWSChanged = value => {
    setDashboardWorkspaceId(value)
    const formValues = form.getFieldsValue()
    formValues.dashboardReportId = ''
    form.setFieldsValue(formValues)
  }

  const handleClearDashboard = () => {
    const formValues = form.getFieldsValue()
    formValues.dashboardReportId = ''
    formValues.dashboardWorkspaceId = ''
    form.setFieldsValue(formValues)
  }

  const professionalWSChanged = value => {
    setProfessionalWorkspaceId(value)
    const formValues = form.getFieldsValue()
    formValues.professionalReportId = ''
    form.setFieldsValue(formValues)
  }

  const handleClearProfessional = () => {
    const formValues = form.getFieldsValue()
    formValues.professionalReportId = ''
    formValues.professionalWorkspaceId = ''
    form.setFieldsValue(formValues)
  }

  const dashboardDropDown = useMemo(
    () => (
      <Select
        disabled={dashboardWorkspaceId == '' || dashboardWorkspaceId == null}
        loading={biDashboardReports.isLoading}
      >
        {biDashboardReports?.data
          ?.filter(
            dashRep =>
              !dashRep.reportName.toLowerCase().includes('usage metrics report')
          )
          ?.sort((a, b) => a.reportName.localeCompare(b.reportName))
          .map(dashRep => (
            <Select.Option key={dashRep.reportId} value={dashRep.reportId}>
              {dashRep.reportName}
            </Select.Option>
          ))}
      </Select>
    ),
    [dashboardWorkspaceId, biDashboardReports]
  )

  const professionalDropDown = useMemo(
    () => (
      <Select
        disabled={
          professionalWorkspaceId == '' || professionalWorkspaceId == null
        }
        loading={biProfessionalReports.isLoading}
      >
        {biProfessionalReports?.data
          ?.filter(
            dashRep =>
              !dashRep.reportName.toLowerCase().includes('usage metrics report')
          )
          ?.sort((a, b) => a.reportName.localeCompare(b.reportName))
          .map(professionalRep => (
            <Select.Option
              key={professionalRep.reportId}
              value={professionalRep.reportId}
            >
              {professionalRep.reportName}
            </Select.Option>
          ))}
      </Select>
    ),
    [professionalWorkspaceId, biProfessionalReports]
  )

  if (!biReportsUserPermissions.isFetched) {
    return <ScreenSpin />
  }

  return (
    <FormProvider>
      <SectionProvider>
        <BodyContentColumn>
          <Form
            form={form}
            name="biReportEdit"
            initialValues={formValues}
            onFinish={onSubmit}
            layout="vertical"
            requiredMark={false}
          >
            <InsetSection className="InsetSection">
              <Typography.Title level={3} style={{ margin: 0 }}>
                Reports
              </Typography.Title>
              <Row justify="space-around" align="middle" gutter={[8, 8]}>
                <Col span={5}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Dashboard
                  </Typography.Title>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="dashboardWorkspaceId"
                    label="Dashboard Workspace"
                  >
                    {workspaceDropDown(dashboardWSChanged)}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="dashboardReportId" label="Dashboard Report">
                    {dashboardDropDown}
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Button
                    shape="circle"
                    icon={<Icons.CloseOutlined />}
                    onClick={handleClearDashboard}
                    style={{ marginTop: '6px' }}
                  />
                </Col>
              </Row>
              <Row justify="space-around" align="middle" gutter={[8, 8]}>
                <Col span={5}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Professional
                  </Typography.Title>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="professionalWorkspaceId"
                    label="Professional Workspace"
                  >
                    {workspaceDropDown(professionalWSChanged)}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="professionalReportId"
                    label="Professional Report"
                  >
                    {professionalDropDown}
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Button
                    shape="circle"
                    icon={<Icons.CloseOutlined />}
                    onClick={handleClearProfessional}
                    style={{ marginTop: '6px' }}
                  />
                </Col>
              </Row>
            </InsetSection>
            <InsetSection className="InsetSection">
              <Row justify="space-around" align="middle" gutter={[3, 3]}>
                <Col span={8}>
                  <Typography.Title level={3} style={{ margin: 0 }}>
                    User Access
                  </Typography.Title>
                </Col>
                <Col span={9} />
                <Col span={5}>
                  <Typography.Text>Hide Qflow users:</Typography.Text>
                </Col>
                <Col span={2}>
                  <Switch
                    defaultChecked={hideQflowUsers}
                    onChange={() => setHideQflowUsers(!hideQflowUsers)}
                  />
                </Col>
              </Row>
              <Form.List name="userPermissions">
                {fields => (
                  <UserAccessTable>
                    <thead className="ant-table-thead">
                      <tr>
                        <th id="userName" className="ant-table-cell">
                          User Name
                        </th>
                        <th id="email" className="ant-table-cell">
                          Email
                        </th>
                        <th id="dashboard" className="ant-table-cell">
                          Dashboard
                        </th>
                        <th id="professional" className="ant-table-cell">
                          Professional
                        </th>
                      </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                      {fields.map((field, index) => (
                        <tr
                          className="ant-table-row ant-table-row-level-0"
                          key={field.key}
                          style={{
                            display:
                              hideQflowUsers &&
                              formValues.userPermissions[
                                index
                              ]?.email?.includes('@qualisflow.com')
                                ? 'none'
                                : 'table-row'
                          }}
                        >
                          <td className="ant-table-cell">
                            {formValues.userPermissions[index].displayName}
                          </td>
                          <td className="ant-table-cell">
                            {formValues.userPermissions[index].email}
                          </td>
                          <td
                            className="ant-table-cell"
                            style={{ textAlign: 'center' }}
                          >
                            <Form.Item
                              name={[index, 'dashboardEnabled']}
                              valuePropName="checked"
                            >
                              <Checkbox />
                            </Form.Item>
                          </td>
                          <td
                            className="ant-table-cell"
                            style={{ textAlign: 'center' }}
                          >
                            <Form.Item
                              name={[index, 'professionalEnabled']}
                              valuePropName="checked"
                            >
                              <Checkbox />
                            </Form.Item>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </UserAccessTable>
                )}
              </Form.List>
            </InsetSection>

            <Row justify="center">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ width: '8rem' }}
                >
                  Save
                </Button>

                <Button onClick={navigateToList} style={{ width: '8rem' }}>
                  Back to list
                </Button>
              </Space>
            </Row>
          </Form>
        </BodyContentColumn>
      </SectionProvider>
    </FormProvider>
  )
}

BIReportEditForm.propTypes = {
  biReports: PropTypes.array.isRequired,
  account: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired
}

const UserAccessTable = styled.table`
  table-layout: fixed;
  width: 100%;
  th,
  td {
    width: 100%;
  }

  #email {
    width: 240%;
  }
`

const BLANK_REPORT = {
  accountId: Guid.Empty,
  reportId: '',
  reportName: '',
  reportType: '',
  workspaceId: ''
}

function getReport(biReports, reportType) {
  const report = biReports.find(rep => rep.reportType.toLowerCase() == reportType)
  return report ?? BLANK_REPORT
}

function transformBIReportToFormData(
  dashboardReport,
  professionalReport,
  biReportsUserPermissions,
  availableUsers
) {
  const userPermissions = []

  availableUsers.forEach(user => {
    const professionalPermission = biReportsUserPermissions.find(
      perm =>
        perm.userId == user.userId &&
        perm.reportType.toLowerCase() == 'professional'
    )
    const dashboardPermission = biReportsUserPermissions.find(
      perm =>
        perm.userId == user.userId &&
        perm.reportType.toLowerCase() == 'dashboard'
    )
    userPermissions.push({
      userId: user.userId,
      displayName: user.displayName,
      professionalPermissionId:
        professionalPermission?.permissionId ?? Guid.Empty,
      professionalEnabled: professionalPermission?.accessEnabled ?? true,
      dashboardPermissionId: dashboardPermission?.permissionId ?? Guid.Empty,
      dashboardEnabled: dashboardPermission?.accessEnabled ?? true,
      email: user.email
    })
  })

  return {
    dashboardReportId: dashboardReport.reportId,
    dashboardReportName: dashboardReport.reportName,
    dashboardWorkspaceId: dashboardReport.workspaceId,
    professionalReportId: professionalReport.reportId,
    professionalReportName: professionalReport.reportName,
    professionalWorkspaceId: professionalReport.workspaceId,
    userPermissions
  }
}

function transformFormDataForSubmission(
  accountId,
  values,
  biDashboardReports,
  biProfessionalReports
) {
  const biReports = []
  const userPermissions = []
  if (values.dashboardReportId != '' && values.dashboardWorkspaceId != '') {
    biReports.push({
      reportId: values.dashboardReportId,
      reportName: biDashboardReports.find(
        rep => rep.reportId == values.dashboardReportId
      )?.reportName,
      workspaceId: values.dashboardWorkspaceId,
      reportType: 'Dashboard',
      accountId: accountId
    })
  }
  if (
    values.professionalReportId != '' &&
    values.professionalWorkspaceId != ''
  ) {
    biReports.push({
      reportId: values.professionalReportId,
      reportName: biProfessionalReports.find(
        rep => rep.reportId == values.professionalReportId
      )?.reportName,
      workspaceId: values.professionalWorkspaceId,
      reportType: 'Professional',
      accountId: accountId
    })
  }
  values.userPermissions.forEach(up => {
    userPermissions.push({
      userId: up.userId,
      permissionId: up.dashboardPermissionId,
      accessEnabled: up.dashboardEnabled,
      reportType: 'Dashboard'
    })
    userPermissions.push({
      userId: up.userId,
      permissionId: up.professionalPermissionId,
      accessEnabled: up.professionalEnabled,
      reportType: 'Professional'
    })
  })
  return { biReports, userPermissions }
}
